// Asset list helpers shared by the store selector and the views that build
// the merged list themselves (for re-render reasons — `selectAllAssets`
// returns a fresh array per call and cannot be subscribed to directly).
//
// Every call site MUST go through `buildAssetList` so custom assets carry
// `_isCustom: true`. Two views (STSView 2026-08-13, RecordingStudio — GitHub
// #1 point 4) each re-merged the list by hand and dropped the tag, which
// silently disabled custom-asset behaviour downstream (upload folder name,
// recording-overlay video).

import type { Asset, ToolConfig } from '@shared/types';

/** Apply vanilla's predefined free-choice voice-fallback to a config-asset
 *  list. Mirrors legacy/index.html line 4014:
 *    `: (CONFIG.voices?.find(v => v.id === asset.voiceId) || CONFIG.voices?.[0]);`
 *  Vanilla resolves the fallback at every read site (5 locations: 4014, 4353,
 *  5498, 5622, 9869). Doing it once on the merged list — and reusing this
 *  helper in the few call sites that build their own merge for re-render
 *  reasons (TTSView/STSView/BottomBar/UploadSummary/AppShell/FreeAssetHeader)
 *  — achieves the same effect.
 *
 *  Custom assets are NOT auto-fallback'd — they require explicit selection via
 *  FreeAssetHeader's voice picker (vanilla parity, line 4012-4013: the custom
 *  branch returns null when voiceId empty). Pass only the predefined
 *  `config.assets` slice to this helper. */
export function applyPredefinedFreeVoiceFallback(
  cfgAssets: Asset[] | undefined,
  voices: ToolConfig['voices'] | undefined,
): Asset[] {
  if (!cfgAssets) return [];
  const fallbackVoiceId = voices?.[0]?.id;
  if (!fallbackVoiceId) return cfgAssets;
  return cfgAssets.map((a) => {
    if (a.assetType !== 'free') return a;
    const hasVoice = (a.voiceIds && a.voiceIds.length > 0) || !!a.voiceId;
    if (hasVoice) return a;
    return { ...a, voiceIds: [fallbackVoiceId] };
  });
}

/** Combine config assets + session custom assets into the ordered list that
 *  the UI iterates. Custom assets always come after admin-defined ones and are
 *  tagged with `_isCustom: true` so downstream UI (video toggle, voice picker)
 *  can branch on it. Predefined free-choice assets get a voiceId fallback —
 *  see `applyPredefinedFreeVoiceFallback` for the vanilla reference. */
/** Combine config assets + session custom assets into the ordered list that
 *  the UI iterates. Custom assets always come after admin-defined ones and are
 *  tagged with `_isCustom: true` so downstream UI (video toggle, voice picker)
 *  can branch on it. Predefined free-choice assets get a voiceId fallback —
 *  see `applyPredefinedFreeVoiceFallback` for the vanilla reference. */
export function buildAssetList(
  cfgAssets: Asset[] | undefined,
  voices: ToolConfig['voices'] | undefined,
  customAssets: Asset[],
): Asset[] {
  const cfgWithFallback = applyPredefinedFreeVoiceFallback(cfgAssets, voices);
  const custom = customAssets.map((ca) => ({
    ...ca,
    assetType: 'free' as const,
    _isCustom: true as const,
  }));
  return [...cfgWithFallback, ...custom];
}
